import { useState, useEffect, useCallback, useRef, ReactNode } from 'react';
import { useAuth, authService } from '@bri/shared-components';
import { workspaceService, type Workspace as APIWorkspace, type CreateWorkspaceRequest } from '@/lib/services/workspaceService';
import { getErrorMessage } from '@/lib/utils';
import { WorkspaceContext } from './workspaceStore';
import type { Workspace, WorkspaceContextType } from './workspaceTypes';
import type { AuthUser } from '@bri/shared-components';

const CROSS_DOMAIN_WORKSPACE_KEY = 'bri_cross_domain_workspace_id';

interface WorkspaceProviderProps {
  children: ReactNode;
}

function transformWorkspace(apiWorkspace: APIWorkspace): Workspace {
  return {
    workspaceId: apiWorkspace.workspaceId,
    name: apiWorkspace.name,
    description: apiWorkspace.description,
    role: apiWorkspace.userRole || 'viewer',
    createdAt: apiWorkspace.createdAt,
    memberCount: apiWorkspace.memberCount || 1,
    projectCount: apiWorkspace.projectCount || 0,
  };
}

function transformUserWorkspace(userWorkspace: AuthUser['workspaces'][number]): Workspace {
  return {
    workspaceId: userWorkspace.workspaceId,
    name: userWorkspace.name,
    role: userWorkspace.role,
    createdAt: userWorkspace.joinedAt,
    memberCount: 1,
    projectCount: 0,
  };
}

function resolveDefaultWorkspace(
  availableWorkspaces: Workspace[],
  activeWorkspaceId?: string,
): Workspace | undefined {
  if (availableWorkspaces.length === 0) {
    return undefined;
  }

  if (activeWorkspaceId) {
    const activeWorkspace = availableWorkspaces.find(
      (workspace) => workspace.workspaceId === activeWorkspaceId,
    );

    if (activeWorkspace) {
      return activeWorkspace;
    }
  }

  return (
    availableWorkspaces.find((workspace) => workspace.role === 'owner') ||
    availableWorkspaces[0]
  );
}

export function WorkspaceProvider({ children }: WorkspaceProviderProps) {
  const { user } = useAuth();
  const [currentWorkspace, setCurrentWorkspace] = useState<Workspace | null>(null);
  const [workspaces, setWorkspaces] = useState<Workspace[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAutoCreating, setIsAutoCreating] = useState(false);
  const workspaceStateVersion = useRef(0);
  const workspaceFetchSequence = useRef(0);
  const locallyActivatedWorkspaceId = useRef<string | null>(null);

  const activateWorkspace = async (workspace: Workspace) => {
    const result = await authService.switchWorkspace(workspace.workspaceId);

    if (!result.success) {
      throw new Error(result.error || 'Failed to update active workspace in JWT');
    }

    workspaceStateVersion.current += 1;
    setIsLoading(false);
    setIsAutoCreating(false);
    locallyActivatedWorkspaceId.current = workspace.workspaceId;
    setCurrentWorkspace(workspace);
    localStorage.setItem('currentWorkspaceId', workspace.workspaceId);
  };


  // Fetch workspaces from API
  const fetchWorkspaces = useCallback(async () => {
    if (!user) return;
    const requestVersion = workspaceStateVersion.current;
    const requestSequence = ++workspaceFetchSequence.current;
    const isStaleRequest = () =>
      requestVersion !== workspaceStateVersion.current ||
      requestSequence !== workspaceFetchSequence.current;
    
    try {
      setIsLoading(true);
      setIsAutoCreating(false);
      setError(null);
      const fetchedWorkspaces = await workspaceService.listWorkspaces();

      if (isStaleRequest()) {
        return;
      }
      
      if (fetchedWorkspaces.length > 0) {
        const transformedWorkspaces = fetchedWorkspaces.map(transformWorkspace);
        setWorkspaces(transformedWorkspaces);
        
        const crossDomainWorkspaceId = localStorage.getItem(CROSS_DOMAIN_WORKSPACE_KEY);
        // A successful local switch is newer than the auth profile, which may not
        // refresh immediately after the JWT changes.
        const authWorkspaceId = user.currentWorkspace?.workspaceId;
        const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
        const defaultWorkspace = resolveDefaultWorkspace(
          transformedWorkspaces,
          crossDomainWorkspaceId ||
            locallyActivatedWorkspaceId.current ||
            authWorkspaceId ||
            savedWorkspaceId ||
            undefined,
        );
        
        if (defaultWorkspace) {
          if (crossDomainWorkspaceId) {
            locallyActivatedWorkspaceId.current = defaultWorkspace.workspaceId;
          }
          setCurrentWorkspace(defaultWorkspace);
          localStorage.setItem('currentWorkspaceId', defaultWorkspace.workspaceId);
        }

        localStorage.removeItem(CROSS_DOMAIN_WORKSPACE_KEY);
      } else {
        // No workspaces found - the backend migration logic should have created one
        // Let's wait a moment and try fetching again, as the backend might be auto-creating
        setIsAutoCreating(true);
        
        // Wait a bit for backend auto-creation to complete
        setTimeout(async () => {
          try {
            const retryWorkspaces = await workspaceService.listWorkspaces();
            if (isStaleRequest()) {
              return;
            }
            if (retryWorkspaces.length > 0) {
              const transformedRetryWorkspaces = retryWorkspaces.map(transformWorkspace);
              setWorkspaces(transformedRetryWorkspaces);
              const crossDomainWorkspaceId = localStorage.getItem(CROSS_DOMAIN_WORKSPACE_KEY);
              const defaultWorkspace = resolveDefaultWorkspace(
                transformedRetryWorkspaces,
                crossDomainWorkspaceId || user.currentWorkspace?.workspaceId,
              );

              if (!defaultWorkspace) {
                setError('No workspaces found. Please contact support.');
                return;
              }

              setCurrentWorkspace(defaultWorkspace);
              localStorage.setItem('currentWorkspaceId', defaultWorkspace.workspaceId);
              localStorage.removeItem(CROSS_DOMAIN_WORKSPACE_KEY);
            } else {
              setError('No workspaces found. Please contact support.');
            }
          } catch (retryError) {
            if (isStaleRequest()) {
              return;
            }
            console.error('WorkspaceContext: Retry failed:', retryError);
            setError('Failed to load workspaces. Please try again.');
          } finally {
            if (!isStaleRequest()) {
              setIsAutoCreating(false);
            }
          }
        }, 2000); // Wait 2 seconds for backend auto-creation
      }
    } catch (error: unknown) {
      if (isStaleRequest()) {
        return;
      }
      console.error('WorkspaceContext: Error fetching workspaces:', error);
      const errorMessage = getErrorMessage(error);
      setError(errorMessage || 'Failed to load workspaces');
    } finally {
      if (!isStaleRequest()) {
        setIsLoading(false);
      }
    }
  }, [user]);

  // Initialize workspaces when user is available
  useEffect(() => {
    workspaceStateVersion.current += 1;

    if (user) {
      if (user.workspaces.length > 0) {
        const profileWorkspaces = user.workspaces.map(transformUserWorkspace);
        const crossDomainWorkspaceId = localStorage.getItem(CROSS_DOMAIN_WORKSPACE_KEY);
        const savedWorkspaceId = localStorage.getItem('currentWorkspaceId');
        const defaultWorkspace = resolveDefaultWorkspace(
          profileWorkspaces,
          crossDomainWorkspaceId || user.currentWorkspace?.workspaceId || savedWorkspaceId || undefined,
        );

        setWorkspaces(profileWorkspaces);
        if (defaultWorkspace) {
          setCurrentWorkspace(defaultWorkspace);
          localStorage.setItem('currentWorkspaceId', defaultWorkspace.workspaceId);
        }
        setIsLoading(false);
        setError(null);
      }

      // The auth profile is a useful immediate cache, but invitations can change
      // workspace membership while the session is open. Always reconcile it with
      // the live API so newly shared workspaces appear without a logout.
      void fetchWorkspaces();
    } else {
      // Clear workspace data when user logs out
      setCurrentWorkspace(null);
      setWorkspaces([]);
      setIsLoading(false);
      setError(null);
      locallyActivatedWorkspaceId.current = null;
      localStorage.removeItem('currentWorkspaceId');
      localStorage.removeItem(CROSS_DOMAIN_WORKSPACE_KEY);
    }
  }, [user, fetchWorkspaces]);

  const switchWorkspace = async (workspaceId: string) => {
    try {
      const workspace = workspaces.find(ws => ws.workspaceId === workspaceId);
      if (!workspace) {
        throw new Error('Workspace not found');
      }

      await activateWorkspace(workspace);
    } catch (error: unknown) {
      console.error('WorkspaceContext: Error switching workspace:', error);
      setError(getErrorMessage(error) || 'Failed to switch workspace');
      throw error;
    }
  };

  const createWorkspace = async (workspaceData: CreateWorkspaceRequest) => {
    try {
      setError(null);
      
      const newApiWorkspace = await workspaceService.createWorkspace(workspaceData);
      const newWorkspace = transformWorkspace(newApiWorkspace);
      
      // The create has committed, so any earlier list response is now stale even
      // while Cognito token alignment is still in progress.
      workspaceStateVersion.current += 1;
      setIsLoading(false);
      setIsAutoCreating(false);

      // Add to local state so the workspace exists in memory, then align the token.
      setWorkspaces(prev => [...prev, newWorkspace]);
      await activateWorkspace(newWorkspace);
    } catch (error: unknown) {
      console.error('WorkspaceContext: Error creating workspace:', error);
      setError(getErrorMessage(error) || 'Failed to create workspace');
      throw error;
    }
  };

  const createDefaultWorkspaceManually = async () => {
    if (!user) throw new Error('No user available');
    
    try {
      setError(null);
      
      const defaultApiWorkspace = await workspaceService.createDefaultWorkspace(user.email);
      const defaultWorkspace = transformWorkspace(defaultApiWorkspace);
      
      // Add to local state, then make it the active JWT workspace too.
      setWorkspaces(prev => [...prev, defaultWorkspace]);
      await activateWorkspace(defaultWorkspace);
    } catch (error: unknown) {
      console.error('WorkspaceContext: Error manually creating default workspace:', error);
      setError(getErrorMessage(error) || 'Failed to create default workspace');
      throw error;
    }
  };

  const refreshWorkspaces = async () => {
    await fetchWorkspaces();
  };

  const value: WorkspaceContextType = {
    currentWorkspace,
    workspaces,
    switchWorkspace,
    createWorkspace,
    createDefaultWorkspace: createDefaultWorkspaceManually,
    refreshWorkspaces,
    isLoading,
    isAutoCreating,
    error,
  };

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}
